import React, { useState, useEffect } from "react";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { 
  Download, 
  Upload, 
  Loader2, 
  Info, 
  Settings, 
  RefreshCw, 
  Check, 
  Eye, 
  EyeOff, 
  Type, 
  Sliders, 
  Calendar,
  Trash2,
  Maximize2,
  Sparkles
} from "lucide-react";
import { DateTime } from "luxon";
import { Pharmacy, Shift } from "./types";

import { adminFetch } from "./api";

type PdfTemplateOrientation = "horizontal" | "vertical";

// Las plantillas se guardan en Firestore a través del servidor (requiere sesión de administrador).
async function pdfTemplateRequest(orientation: PdfTemplateOrientation, init?: RequestInit): Promise<any> {
  const res = await adminFetch(`/api/pdf-template/${orientation}`, init);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }
  return res.json();
}

async function fetchPdfTemplate(orientation: PdfTemplateOrientation): Promise<string | null> {
  const data = await pdfTemplateRequest(orientation);
  return data.base64 || null;
}

async function storePdfTemplate(orientation: PdfTemplateOrientation, base64: string): Promise<void> {
  await pdfTemplateRequest(orientation, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ base64 }),
  });
}

async function removePdfTemplate(orientation: PdfTemplateOrientation): Promise<void> {
  await pdfTemplateRequest(orientation, { method: "DELETE" });
}

// Standard PDF dimension: Landscape A4 is 841.89 x 595.28 points
const PDF_WIDTH = 841.89;
const PDF_HEIGHT = 595.28;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || "application/pdf";
  const bstr = atob(arr[1] || "");
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

async function saveTemplateToDB(file: File): Promise<void> {
  try {
    const base64 = await fileToBase64(file);
    await storePdfTemplate("horizontal", base64);
  } catch (e) {
    console.error("Failed to save template to DB", e);
  }
}

async function loadTemplateFromDB(): Promise<File | null> {
  try {
    const base64 = await fetchPdfTemplate("horizontal");
    if (base64) {
      return base64ToFile(base64, "template.pdf");
    }
  } catch (e) {
    console.error("Failed to load template from DB", e);
  }
  return null;
}

async function deleteTemplateFromDB(): Promise<void> {
  try {
    await removePdfTemplate("horizontal");
  } catch (e) {
    console.error("Failed to delete template from DB", e);
  }
}

async function saveTemplateVerticalToDB(file: File): Promise<void> {
  try {
    const base64 = await fileToBase64(file);
    await storePdfTemplate("vertical", base64);
  } catch (e) {
    console.error("Failed to save vertical template to DB", e);
  }
}

async function loadTemplateVerticalFromDB(): Promise<File | null> {
  try {
    const base64 = await fetchPdfTemplate("vertical");
    if (base64) {
      return base64ToFile(base64, "template_vertical.pdf");
    }
  } catch (e) {
    console.error("Failed to load vertical template from DB", e);
  }
  return null;
}

async function deleteTemplateVerticalFromDB(): Promise<void> {
  try {
    await removePdfTemplate("vertical");
  } catch (e) {
    console.error("Failed to delete vertical template from DB", e);
  }
}

interface PDFPreset {
  // Fonts
  dayFont: string; 
  dayFontSize: number;
  dayFontBold: boolean;
  
  nameFont: string; 
  nameFontSize: number;
  nameFontBold: boolean;
  
  infoFont: string; 
  infoFontSize: number;
  infoFontBold: boolean;

  // Layout positions (in points)
  colWidth: number;
  startX: number;
  colGap: number;

  // Header Blue Box coordinates
  headerBoxBottom: number;
  headerBoxHeight: number;

  // Pharmacy Green Box coordinates
  pharmacyBoxBottom: number;
  pharmacyBoxHeight: number;

  // Spacing inside pharmacy box
  lineGap: number;
  pharmacyGap: number;

  vStartX: number;
  vColWidth: number;
  vColGap: number;
  vHeaderBoxBottomR1: number;
  vHeaderBoxBottomR2: number;
  vHeaderBoxHeight: number;
  vPharmacyBoxBottomR1: number;
  vPharmacyBoxBottomR2: number;
  vPharmacyBoxHeight: number;
}



const DEFAULT_PRESET: PDFPreset = {
  dayFont: "rounded",
  dayFontSize: 22,
  dayFontBold: true,
  
  nameFont: "helvetica",
  nameFontSize: 14,
  nameFontBold: true,
  
  infoFont: "helvetica",
  infoFontSize: 13,
  infoFontBold: false,

  colWidth: 180,
  startX: 47,
  colGap: 11,

  headerBoxBottom: 355,
  headerBoxHeight: 40,

  pharmacyBoxBottom: 115,
  pharmacyBoxHeight: 225,

  lineGap: 3,
  pharmacyGap: 9,
  
  vStartX: 57,
  vColWidth: 227,
  vColGap: 28,
  vHeaderBoxBottomR1: 711,
  vHeaderBoxBottomR2: 380,
  vHeaderBoxHeight: 23,
  vPharmacyBoxBottomR1: 430,
  vPharmacyBoxBottomR2: 99,
  vPharmacyBoxHeight: 281
};

interface ScaledPresetValues {
  nameFontSize: number;
  infoFontSize: number;
  lineGap: number;
  pharmacyGap: number;
}

function getScaledPresetValues(dayPharmacies: Pharmacy[], preset: PDFPreset): ScaledPresetValues {
  const { nameFontSize, infoFontSize, lineGap, pharmacyGap, pharmacyBoxHeight } = preset;
  
  let actualNameFontSize = nameFontSize;
  let actualInfoFontSize = infoFontSize;
  let actualLineGap = lineGap;
  let actualPharmacyGap = pharmacyGap;

  let pharmacyHeights = dayPharmacies.map(pharm => {
    const hasPhone = !!pharm.telefono;
    return actualNameFontSize + actualLineGap + actualInfoFontSize + (hasPhone ? actualLineGap + actualInfoFontSize : 0);
  });
  let totalHeightsSum = pharmacyHeights.reduce((sum, h) => sum + h, 0);
  let totalGapsSum = (dayPharmacies.length - 1) * actualPharmacyGap;
  let totalBlockHeight = totalHeightsSum + totalGapsSum;

  const maxAllowedHeight = pharmacyBoxHeight - 12;
  if (totalBlockHeight > maxAllowedHeight) {
    const scale = maxAllowedHeight / totalBlockHeight;
    actualNameFontSize = Math.max(10, Math.floor(nameFontSize * scale));
    actualInfoFontSize = Math.max(9, Math.floor(infoFontSize * scale));
    actualLineGap = Math.max(2, Math.floor(lineGap * scale));
    actualPharmacyGap = Math.max(6, Math.floor(pharmacyGap * scale));
  }

  return {
    nameFontSize: actualNameFontSize,
    infoFontSize: actualInfoFontSize,
    lineGap: actualLineGap,
    pharmacyGap: actualPharmacyGap
  };
}

export default function PDFGenerator({ shifts, pharmacies }: { shifts: Shift[], pharmacies: Pharmacy[] }) {
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(true);
  
  const [templateFileVertical, setTemplateFileVertical] = useState<File | null>(null);
  const [isLoadingTemplateVertical, setIsLoadingTemplateVertical] = useState<boolean>(true);

  const [isAdjustmentsOpen, setIsAdjustmentsOpen] = useState<boolean>(false);

  const [targetMonth, setTargetMonth] = useState<string>(
    DateTime.now().toFormat("yyyy-MM")
  );
  const [blocks, setBlocks] = useState<{startDate: string, selected: boolean}[]>([]);

  useEffect(() => {
    const startOfMonth = DateTime.fromISO(`${targetMonth}-01`);
    const newBlocks = [];
    let currentStart = startOfMonth;
    while (currentStart.weekday !== 5) {
      currentStart = currentStart.minus({ days: 1 });
    }
    let isFridayStart = true;
    while (true) {
      newBlocks.push({ startDate: currentStart.toFormat("yyyy-MM-dd"), selected: true });
      if (isFridayStart) {
        currentStart = currentStart.plus({ days: 4 });
      } else {
        currentStart = currentStart.plus({ days: 3 });
      }
      isFridayStart = !isFridayStart;
      if (currentStart.month !== startOfMonth.month && currentStart > startOfMonth.endOf('month')) {
        break;
      }
    }
    setBlocks(newBlocks);
  }, [targetMonth]);

  
  // Customization presets
  const [preset, setPreset] = useState<PDFPreset>(DEFAULT_PRESET);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [status, setStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [showGuides, setShowGuides] = useState<boolean>(true);
  const [showMockBg, setShowMockBg] = useState<boolean>(true);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // Active View Mode: 'A4' (All columns scaled down) vs 'single' (1 day column at real physical size)
  const [viewMode, setViewMode] = useState<"A4" | "single">("single");
  const [focusedColumnIndex, setFocusedColumnIndex] = useState<number>(0);

  // Load custom preset and persistent template PDF file on mount
  useEffect(() => {
    // 1. Load presets from localStorage
    try {
      const saved = localStorage.getItem("turno_tandil_pdf_preset");
      if (saved) {
        const parsed = JSON.parse(saved);
        setPreset({ ...DEFAULT_PRESET, ...parsed });
      }
    } catch (e) {
      console.error("Error loading saved preset:", e);
    }

    // 2. Load persistent template PDF file from IndexedDB
    const loadSavedPDF = async () => {
      try {
        const savedFile = await loadTemplateFromDB();
        if (savedFile) {
          setTemplateFile(savedFile);
        }
      } catch (e) {
        console.error("Error loading template from DB:", e);
      } finally {
        setIsLoadingTemplate(false);
      }
    };
    
    const loadSavedVerticalPDF = async () => {
      try {
        const savedFile = await loadTemplateVerticalFromDB();
        if (savedFile) {
          setTemplateFileVertical(savedFile);
        }
      } catch (e) {
        console.error("Error loading vertical template from DB:", e);
      } finally {
        setIsLoadingTemplateVertical(false);
      }
    };

    loadSavedPDF();
    loadSavedVerticalPDF();
  }, []);

  // Save preset to localStorage
  const handleSavePreset = () => {
    try {
      localStorage.setItem("turno_tandil_pdf_preset", JSON.stringify(preset));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (e) {
      console.error("Error saving preset:", e);
    }
  };

  // Restore defaults
  const handleRestoreDefaults = () => {
    if (window.confirm("¿Seguro que deseas restablecer la posición y fuentes predeterminadas?")) {
      setPreset(DEFAULT_PRESET);
    }
  };

  // Handle PDF file upload
  const handleFileUpload = async (file: File) => {
    setTemplateFile(file);
    try {
      await saveTemplateToDB(file);
    } catch (e) {
      console.error("Failed to save PDF template locally:", e);
    }
  };

  // Delete saved PDF template
  const handleDeleteTemplate = async () => {
    if (window.confirm("¿Seguro que deseas eliminar la plantilla guardada? Deberás subirla de nuevo para exportar.")) {
      setTemplateFile(null);
      try {
        await deleteTemplateFromDB();
      } catch (e) {
        console.error("Failed to delete PDF template:", e);
      }
    }
  };

  const handleFileUploadVertical = async (file: File) => {
    setTemplateFileVertical(file);
    try {
      await saveTemplateVerticalToDB(file);
    } catch (e) {
      console.error("Failed to save vertical PDF template locally:", e);
    }
  };

  const handleDeleteTemplateVertical = async () => {
    if (window.confirm("¿Seguro que deseas eliminar la plantilla vertical guardada? Deberás subirla de nuevo para exportar.")) {
      setTemplateFileVertical(null);
      try {
        await deleteTemplateVerticalFromDB();
      } catch (e) {
        console.error("Failed to delete vertical PDF template:", e);
      }
    }
  };

  const getPDFFont = async (pdfDoc: PDFDocument, fontKey: string, isBold: boolean) => {
    switch (fontKey) {
      case "times":
        return await pdfDoc.embedFont(isBold ? StandardFonts.TimesRomanBold : StandardFonts.TimesRoman);
      case "courier":
        return await pdfDoc.embedFont(isBold ? StandardFonts.CourierBold : StandardFonts.Courier);
      case "rounded":
      case "helvetica":
      default:
        return await pdfDoc.embedFont(isBold ? StandardFonts.HelveticaBold : StandardFonts.Helvetica);
    }
  };

  const getFontFamilyCSS = (fontKey: string) => {
    switch (fontKey) {
      case "rounded":
        return '"Nunito", "Quicksand", "Arial Rounded MT Bold", sans-serif';
      case "times":
        return '"Georgia", "Times New Roman", serif';
      case "courier":
        return '"Fira Code", "Courier New", Courier, monospace';
      case "helvetica":
      default:
        return 'Inter, system-ui, -apple-system, sans-serif';
    }
  };

  // Fetch shifts for the 4 days starting from the first block
  const previewStartDateStr = blocks.length > 0 ? blocks[0].startDate : DateTime.now().toFormat("yyyy-MM-dd");
  const startDt = DateTime.fromISO(previewStartDateStr);
  const targetDates = [
    startDt.toFormat("yyyy-MM-dd"),
    startDt.plus({ days: 1 }).toFormat("yyyy-MM-dd"),
    startDt.plus({ days: 2 }).toFormat("yyyy-MM-dd"),
    startDt.plus({ days: 3 }).toFormat("yyyy-MM-dd"),
  ];

  const getPharmaciesForIndex = (index: number) => {
    const shift = shifts.find(s => s.date === targetDates[index]);
    return shift 
       ? (shift.pharmacyIds.map(id => pharmacies.find(p => p.id === id)).filter(Boolean) as Pharmacy[])
       : [];
  };

    const handleGenerate = async (isVertical = false) => {
    const currentTemplateFile = isVertical ? templateFileVertical : templateFile;
    if (!currentTemplateFile) {
      setStatus({ success: false, message: `Falta subir o tener guardada la plantilla ${isVertical ? 'vertical' : 'horizontal'} en formato PDF.` });
      return;
    }
    
    setIsGenerating(true);
    setStatus(null);

    try {
      const templateBuffer = await currentTemplateFile.arrayBuffer();
      const templatePdfDoc = await PDFDocument.load(templateBuffer);
      const outPdfDoc = await PDFDocument.create();
      
      const {
        dayFont, dayFontSize, dayFontBold,
        nameFont, nameFontSize, nameFontBold,
        infoFont, infoFontSize, infoFontBold,
        colWidth, startX, colGap,
        headerBoxBottom, headerBoxHeight,
        pharmacyBoxBottom, pharmacyBoxHeight,
        lineGap, pharmacyGap,
        vStartX, vColWidth, vColGap, 
        vHeaderBoxBottomR1, vHeaderBoxBottomR2, vHeaderBoxHeight,
        vPharmacyBoxBottomR1, vPharmacyBoxBottomR2, vPharmacyBoxHeight
      } = preset;

      const embeddedFonts: Record<string, any> = {};
      const getCachedPDFFont = async (fontKey: string, isBold: boolean) => {
        const cacheKey = `${fontKey}_${isBold ? "bold" : "regular"}`;
        if (embeddedFonts[cacheKey]) {
          return embeddedFonts[cacheKey];
        }
        const font = await getPDFFont(outPdfDoc, fontKey, isBold);
        embeddedFonts[cacheKey] = font;
        return font;
      };

      for (let bIdx = 0; bIdx < blocks.length; bIdx++) {
        if (!blocks[bIdx].selected) continue;
        const blockStartDateStr = blocks[bIdx].startDate;
        const startDt = DateTime.fromISO(blockStartDateStr);
        
        const [copiedPage] = await outPdfDoc.copyPages(templatePdfDoc, [0]);
        outPdfDoc.addPage(copiedPage);



        for (let i = 0; i < 4; i++) {
          const dt = startDt.plus({ days: i });
          const dayStr = dt.setLocale("es").toFormat("cccc d");
          const trimmedDayStr = dayStr.trim();
          const capitalizedDayStr = trimmedDayStr.charAt(0).toUpperCase() + trimmedDayStr.slice(1);
          
          const targetDateStr = dt.toFormat("yyyy-MM-dd");
          const shift = shifts.find(s => s.date === targetDateStr);
          const dayPharmacies = shift 
             ? (shift.pharmacyIds.map(id => pharmacies.find(p => p.id === id)).filter(Boolean) as Pharmacy[])
             : [];
             
                    let centerColX;
          let headerCenterY;
          let pharmacyCenterYBase;
          let actualPharmacyBoxHeight = pharmacyBoxHeight;

          if (isVertical) {
            const vCol = i % 2;
            const vRow = Math.floor(i / 2);
            centerColX = vStartX + vCol * (vColWidth + vColGap) + (vColWidth / 2);
            headerCenterY = (vRow === 0 ? vHeaderBoxBottomR1 : vHeaderBoxBottomR2) + (vHeaderBoxHeight / 2);
            pharmacyCenterYBase = (vRow === 0 ? vPharmacyBoxBottomR1 : vPharmacyBoxBottomR2);
            actualPharmacyBoxHeight = vPharmacyBoxHeight;
          } else {
            const xPos = startX + i * (colWidth + colGap);
            centerColX = xPos + (colWidth / 2);
            headerCenterY = headerBoxBottom + headerBoxHeight / 2;
            pharmacyCenterYBase = pharmacyBoxBottom;
          }
             
          const dayFontEmbed = await getCachedPDFFont(dayFont, dayFontBold);
          const textWidth = dayFontEmbed.widthOfTextAtSize(capitalizedDayStr, dayFontSize);
             
          const dayTextY = headerCenterY - (dayFontSize * 0.7) / 2;

          copiedPage.drawText(capitalizedDayStr, {
            x: centerColX - (textWidth / 2),
            y: dayTextY,
            size: dayFontSize,
            font: dayFontEmbed,
            color: rgb(1, 1, 1),
          });

          if (dayPharmacies.length > 0) {
            const nameFontEmbed = await getCachedPDFFont(nameFont, nameFontBold);
            const infoFontEmbed = await getCachedPDFFont(infoFont, infoFontBold);

            const {
              nameFontSize: actualNameFontSize,
              infoFontSize: actualInfoFontSize,
              lineGap: actualLineGap,
              pharmacyGap: actualPharmacyGap
            } = getScaledPresetValues(dayPharmacies, preset);

            const pharmacyHeights = dayPharmacies.map(pharm => {
              const hasPhone = !!pharm.telefono;
              return actualNameFontSize + actualLineGap + actualInfoFontSize + (hasPhone ? actualLineGap + actualInfoFontSize : 0);
            });
            const totalHeightsSum = pharmacyHeights.reduce((sum, h) => sum + h, 0);
            const totalGapsSum = (dayPharmacies.length - 1) * actualPharmacyGap;
            const totalBlockHeight = totalHeightsSum + totalGapsSum;

            const pharmacyCenterY = pharmacyCenterYBase + actualPharmacyBoxHeight / 2;
            const blockTopY = pharmacyCenterY + totalBlockHeight / 2;

            let currentY = blockTopY;

            for (let j = 0; j < dayPharmacies.length; j++) {
              const pharm = dayPharmacies[j];
              const hasPhone = !!pharm.telefono;

              const name = pharm.nombre.trim().toUpperCase();
              const nameWidth = nameFontEmbed.widthOfTextAtSize(name, actualNameFontSize);
              copiedPage.drawText(name, {
                x: centerColX - (nameWidth / 2),
                y: currentY - actualNameFontSize,
                size: actualNameFontSize,
                font: nameFontEmbed,
                color: rgb(0, 0, 0),
              });
              currentY -= actualNameFontSize + actualLineGap;

              const address = pharm.direccion.trim();
              const addrWidth = infoFontEmbed.widthOfTextAtSize(address, actualInfoFontSize);
              copiedPage.drawText(address, {
                x: centerColX - (addrWidth / 2),
                y: currentY - actualInfoFontSize,
                size: actualInfoFontSize,
                font: infoFontEmbed,
                color: rgb(0.15, 0.15, 0.15),
              });
              currentY -= actualInfoFontSize;

              if (hasPhone && pharm.telefono) {
                currentY -= actualLineGap;
                const phone = pharm.telefono.trim();
                const phoneWidth = infoFontEmbed.widthOfTextAtSize(phone, actualInfoFontSize);
                copiedPage.drawText(phone, {
                  x: centerColX - (phoneWidth / 2),
                  y: currentY - actualInfoFontSize,
                  size: actualInfoFontSize,
                  font: infoFontEmbed,
                  color: rgb(0.15, 0.15, 0.15),
                });
                currentY -= actualInfoFontSize;
              }
              currentY -= actualPharmacyGap;
            }
          }
        }
      }

      const pdfBytes = await outPdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement("a");
      a.href = url;
      a.download = `Farmacias_Turno_${targetMonth}.pdf`;
      a.click();
      
      URL.revokeObjectURL(url);
      
      setStatus({ success: true, message: "¡PDF vectorizado multipágina generado y descargado!" });
    } catch (err: any) {
      console.error(err);
      setStatus({ success: false, message: "Error al generar el PDF: " + err.message });
    } finally {
      setIsGenerating(false);
    }
  };
const updatePreset = (key: keyof PDFPreset, value: any) => {
    setPreset(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-50/50 p-4 md:p-8">
      {/* Dynamic Rounded Google Fonts Import for the Live Preview */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,700;0,800;1,400&family=Quicksand:wght@500;600;700&display=swap');
      `}</style>

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Calendar className="text-emerald-500" size={24} />
              Turnero Impreso
            </h1>
            <p className="text-sm text-slate-500 font-medium">
              Crea el cartel multipágina listo para impresión.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          
          {/* LEFT COLUMN: Tipografia, Tamaños y Plantilla */}
          <div className="space-y-6">
            
            {/* Template File Upload */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span className="w-5 h-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-xs font-extrabold">1</span>
                Plantilla Horizontal
              </h2>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Archivo PDF
                  </label>
                  {templateFile && (
                    <button
                      onClick={handleDeleteTemplate}
                      className="text-[10px] text-rose-600 hover:text-rose-800 font-bold flex items-center gap-1"
                    >
                      <Trash2 size={11} />
                      Eliminar
                    </button>
                  )}
                </div>
                
                {isLoadingTemplate ? (
                  <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 flex items-center justify-center gap-2 text-xs text-slate-400 font-medium">
                    <Loader2 size={14} className="animate-spin" />
                    Cargando plantilla desde Firestore...
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all duration-200 relative ${
                      templateFile
                        ? "border-emerald-500 bg-emerald-50/10"
                        : "border-slate-200 hover:border-slate-300 bg-slate-50"
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileUpload(e.target.files[0]);
                        }
                      }}
                    />
                    <Upload size={20} className="mx-auto text-slate-400 mb-2" />
                    {templateFile ? (
                      <div className="text-sm text-emerald-600 font-bold truncate px-2 flex items-center justify-center gap-1.5">
                        <Check size={14} className="shrink-0" />
                        <span className="truncate">{templateFile.name}</span>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500 font-medium">
                        Haz clic o arrastra tu plantilla .pdf aquí (Se guardará en Firestore)
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span className="w-5 h-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-xs font-extrabold">2</span>
                Plantilla Vertical
              </h2>
                 
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Archivo PDF
                  </label>
                  {templateFileVertical && (
                    <button
                      onClick={handleDeleteTemplateVertical}
                      className="text-[10px] text-rose-600 hover:text-rose-800 font-bold flex items-center gap-1"
                    >
                      <Trash2 size={11} />
                      Eliminar
                    </button>
                  )}
                </div>
                   
                {isLoadingTemplateVertical ? (
                  <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 flex items-center justify-center gap-2 text-xs text-slate-400 font-medium">
                    <Loader2 size={14} className="animate-spin" />
                    Cargando plantilla...
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all duration-200 relative ${
                      templateFileVertical
                        ? "border-emerald-500 bg-emerald-50/10"
                        : "border-slate-200 hover:border-slate-300 bg-slate-50"
                    }`}
                  >
                    <input
                      type="file"
                      accept=".pdf"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileUploadVertical(e.target.files[0]);
                        }
                      }}
                    />
                    <Upload size={20} className="mx-auto text-slate-400 mb-2" />
                    {templateFileVertical ? (
                      <div className="text-sm text-emerald-600 font-bold truncate px-2 flex items-center justify-center gap-1.5">
                        <Check size={14} className="shrink-0" />
                        <span className="truncate">{templateFileVertical.name}</span>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500 font-medium">
                        Haz clic o arrastra tu plantilla vertical .pdf aquí
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Typography Selection */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span className="w-5 h-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-xs font-extrabold">4</span>
                Tipografía & Tamaños
              </h2>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-700">Letra de los Días</span>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                      checked={preset.dayFontBold}
                      onChange={(e) => updatePreset("dayFontBold", e.target.checked)}
                    />
                    Negrita
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="w-full bg-white border border-slate-200 text-xs rounded-lg p-2 font-medium"
                    value={preset.dayFont}
                    onChange={(e) => updatePreset("dayFont", e.target.value)}
                  >
                    <option value="rounded">Arial Rounded MT</option>
                    <option value="helvetica">Helvetica</option>
                    <option value="times">Times</option>
                    <option value="courier">Courier</option>
                  </select>
                  <input
                    type="number"
                    min="10" max="60"
                    className="w-full bg-white border border-slate-200 text-xs rounded-lg p-2 text-center font-bold"
                    value={preset.dayFontSize || 0}
                    onChange={(e) => updatePreset("dayFontSize", parseInt(e.target.value) || 22)}
                  />
                </div>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-700">Nombre Farmacia</span>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                      checked={preset.nameFontBold}
                      onChange={(e) => updatePreset("nameFontBold", e.target.checked)}
                    />
                    Negrita
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="w-full bg-white border border-slate-200 text-xs rounded-lg p-2 font-medium"
                    value={preset.nameFont}
                    onChange={(e) => updatePreset("nameFont", e.target.value)}
                  >
                    <option value="helvetica">Helvetica</option>
                    <option value="rounded">Arial Rounded MT</option>
                    <option value="times">Times</option>
                    <option value="courier">Courier</option>
                  </select>
                  <input
                    type="number"
                    min="8" max="40"
                    className="w-full bg-white border border-slate-200 text-xs rounded-lg p-2 text-center font-bold"
                    value={preset.nameFontSize || 0}
                    onChange={(e) => updatePreset("nameFontSize", parseInt(e.target.value) || 14)}
                  />
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-700">Dirección y Teléfono</span>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                      checked={preset.infoFontBold}
                      onChange={(e) => updatePreset("infoFontBold", e.target.checked)}
                    />
                    Negrita
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="w-full bg-white border border-slate-200 text-xs rounded-lg p-2 font-medium"
                    value={preset.infoFont}
                    onChange={(e) => updatePreset("infoFont", e.target.value)}
                  >
                    <option value="helvetica">Helvetica</option>
                    <option value="rounded">Arial Rounded MT</option>
                    <option value="times">Times</option>
                    <option value="courier">Courier</option>
                  </select>
                  <input
                    type="number"
                    min="6" max="30"
                    className="w-full bg-white border border-slate-200 text-xs rounded-lg p-2 text-center font-bold"
                    value={preset.infoFontSize || 0}
                    onChange={(e) => updatePreset("infoFontSize", parseInt(e.target.value) || 13)}
                  />
                </div>
              </div>
            </div>
            
            {/* Step 4: Positioning (Advanced Accordion) */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button onClick={() => setIsAdjustmentsOpen(!isAdjustmentsOpen)} className="w-full hover:bg-slate-100 transition-colors cursor-pointer flex justify-between items-center p-5 text-sm font-bold text-slate-800 uppercase tracking-wider bg-slate-50/50 border-b border-slate-100">
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-xs font-extrabold">4</span>
                  Alineación y Posición Avanzada
                </span>
                <Settings size={18} className={`text-slate-500 transition-transform ${isAdjustmentsOpen ? 'rotate-90' : ''}`} />
</button>
              
              {isAdjustmentsOpen && (<div className="p-5 space-y-6"><div className="flex flex-col sm:flex-row items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100"><button onClick={handleRestoreDefaults} className="flex-1 w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-slate-600 hover:text-slate-950 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 shadow-sm transition-all"><RefreshCw size={13} />Predeterminados</button><button onClick={handleSavePreset} className="flex-1 w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition-all">{saveSuccess ? <Check size={13} /> : <Sliders size={13} />}{saveSuccess ? 'Preset Guardado' : 'Guardar Preset'}</button></div><div className="space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">Ajustes: Plantilla Horizontal</h3>
                  {/* startX & colWidth */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Margen Izq. ({preset.startX}pt)</label>
                      <input
                        type="range"
                        min="10"
                        max="150"
                        className="w-full accent-emerald-600"
                        value={preset.startX || 0}
                        onChange={(e) => updatePreset("startX", parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Ancho Columna ({preset.colWidth}pt)</label>
                      <input
                        type="range"
                        min="100"
                        max="260"
                        className="w-full accent-emerald-600"
                        value={preset.colWidth || 0}
                        onChange={(e) => updatePreset("colWidth", parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  {/* colGap */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Separación de Columnas ({preset.colGap}pt)</label>
                    <input
                      type="range"
                      min="-20"
                      max="80"
                      className="w-full accent-emerald-600"
                      value={preset.colGap || 0}
                      onChange={(e) => updatePreset("colGap", parseInt(e.target.value))}
                    />
                  </div>

                  {/* headerBoxBottom & headerBoxHeight */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Días: Altura Base ({preset.headerBoxBottom}pt)</label>
                      <input
                        type="range"
                        min="250"
                        max="500"
                        className="w-full accent-emerald-600"
                        value={preset.headerBoxBottom || 0}
                        onChange={(e) => updatePreset("headerBoxBottom", parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Días: Alto Recuadro ({preset.headerBoxHeight}pt)</label>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        className="w-full accent-emerald-600"
                        value={preset.headerBoxHeight || 0}
                        onChange={(e) => updatePreset("headerBoxHeight", parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  {/* pharmacyBoxBottom & pharmacyBoxHeight */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Farmacias: Altura Base ({preset.pharmacyBoxBottom}pt)</label>
                      <input
                        type="range"
                        min="50"
                        max="250"
                        className="w-full accent-emerald-600"
                        value={preset.pharmacyBoxBottom || 0}
                        onChange={(e) => updatePreset("pharmacyBoxBottom", parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Farmacias: Alto Recuadro ({preset.pharmacyBoxHeight}pt)</label>
                      <input
                        type="range"
                        min="100"
                        max="350"
                        className="w-full accent-emerald-600"
                        value={preset.pharmacyBoxHeight || 0}
                        onChange={(e) => updatePreset("pharmacyBoxHeight", parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  {/* Spacing inside pharmacy cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Separación Líneas ({preset.lineGap}pt)</label>
                      <input
                        type="range"
                        min="1"
                        max="15"
                        className="w-full accent-emerald-600"
                        value={preset.lineGap || 0}
                        onChange={(e) => updatePreset("lineGap", parseInt(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Separación Farmacias ({preset.pharmacyGap}pt)</label>
                      <input
                        type="range"
                        min="5"
                        max="45"
                        className="w-full accent-emerald-600"
                        value={preset.pharmacyGap || 0}
                        onChange={(e) => updatePreset("pharmacyGap", parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">Ajustes: Plantilla Vertical (2x2)</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Margen Izq. ({preset.vStartX}pt)</label>
                        <input type="range" min="10" max="150" className="w-full accent-emerald-600" value={preset.vStartX || 0} onChange={(e) => updatePreset("vStartX", parseInt(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Ancho Columna ({preset.vColWidth}pt)</label>
                        <input type="range" min="100" max="350" className="w-full accent-emerald-600" value={preset.vColWidth || 0} onChange={(e) => updatePreset("vColWidth", parseInt(e.target.value))} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Separación de Columnas ({preset.vColGap}pt)</label>
                      <input type="range" min="-20" max="80" className="w-full accent-emerald-600" value={preset.vColGap || 0} onChange={(e) => updatePreset("vColGap", parseInt(e.target.value))} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Días: Altura Base Fila 1 ({preset.vHeaderBoxBottomR1}pt)</label>
                        <input type="range" min="400" max="800" className="w-full accent-emerald-600" value={preset.vHeaderBoxBottomR1 || 0} onChange={(e) => updatePreset("vHeaderBoxBottomR1", parseInt(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Días: Altura Base Fila 2 ({preset.vHeaderBoxBottomR2}pt)</label>
                        <input type="range" min="150" max="500" className="w-full accent-emerald-600" value={preset.vHeaderBoxBottomR2 || 0} onChange={(e) => updatePreset("vHeaderBoxBottomR2", parseInt(e.target.value))} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Farmacias: Altura Base Fila 1 ({preset.vPharmacyBoxBottomR1}pt)</label>
                        <input type="range" min="150" max="500" className="w-full accent-emerald-600" value={preset.vPharmacyBoxBottomR1 || 0} onChange={(e) => updatePreset("vPharmacyBoxBottomR1", parseInt(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Farmacias: Altura Base Fila 2 ({preset.vPharmacyBoxBottomR2}pt)</label>
                        <input type="range" min="10" max="300" className="w-full accent-emerald-600" value={preset.vPharmacyBoxBottomR2 || 0} onChange={(e) => updatePreset("vPharmacyBoxBottomR2", parseInt(e.target.value))} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Alto Recuadro Días ({preset.vHeaderBoxHeight}pt)</label>
                        <input type="range" min="10" max="100" className="w-full accent-emerald-600" value={preset.vHeaderBoxHeight || 0} onChange={(e) => updatePreset("vHeaderBoxHeight", parseInt(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Alto Recuadro Farmacias ({preset.vPharmacyBoxHeight}pt)</label>
                        <input type="range" min="100" max="450" className="w-full accent-emerald-600" value={preset.vPharmacyBoxHeight || 0} onChange={(e) => updatePreset("vPharmacyBoxHeight", parseInt(e.target.value))} />
                      </div>
                    </div>
                  </div>
                </div>
                </div>
            )}
            </div>
          </div>

          {/* CENTER COLUMN: Fechas y Generación */}
          <div className="space-y-6">
            
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span className="w-5 h-5 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-xs font-extrabold">5</span>
                Mes y Hojas (4 Días)
              </h2>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Mes Objetivo
                </label>
                <input
                  type="month"
                  className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-xl focus:ring-emerald-500 focus:border-emerald-500 block p-2.5 font-bold"
                  value={targetMonth}
                  onChange={(e) => setTargetMonth(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Hojas del PDF (Editables)
                </label>
                {blocks.map((block, idx) => (
                  <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${block.selected ? "bg-slate-50 border-slate-200" : "bg-slate-100/50 border-slate-100 opacity-60"}`}>
                    <input
                      type="checkbox"
                      checked={block.selected}
                      onChange={(e) => {
                        const newBlocks = [...blocks];
                        newBlocks[idx].selected = e.target.checked;
                        setBlocks(newBlocks);
                      }}
                      className="rounded border-slate-300 text-emerald-500 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span className="text-xs font-bold text-slate-400 w-16">Hoja {idx + 1}</span>
                    <input
                      type="date"
                      className="flex-1 bg-white border border-slate-200 text-xs rounded px-2 py-1"
                      value={block.startDate}
                      onChange={(e) => {
                        const newBlocks = [...blocks];
                        newBlocks[idx].startDate = e.target.value;
                        setBlocks(newBlocks);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900 text-white rounded-2xl p-5 shadow-md space-y-4">
              <div>
                <h3 className="font-bold text-sm text-emerald-400">Generar Cartel Multipágina</h3>
                <p className="text-xs text-slate-400 mt-1 font-medium leading-relaxed">
                  Exporta el cartel completo del mes ({blocks.length} hojas) en un único PDF vectorizado.
                </p>
              </div>

              <button
                onClick={() => handleGenerate(false)}
                disabled={!templateFile || isGenerating || blocks.length === 0}
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-extrabold py-3 px-4 rounded-xl shadow-md transition-all text-sm cursor-pointer"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Generar PDF Horizontal
                  </>
                )}
              </button>

              <button
                onClick={() => handleGenerate(true)}
                disabled={!templateFileVertical || isGenerating || blocks.length === 0}
                className="w-full flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-extrabold py-3 px-4 rounded-xl shadow-md transition-all text-sm cursor-pointer"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Generar PDF Vertical
                  </>
                )}
              </button>

              {status && (
                <div
                  className={`p-3.5 rounded-xl flex items-start gap-2.5 text-xs font-semibold border ${
                    status.success
                      ? "bg-emerald-950/80 text-emerald-300 border-emerald-900"
                      : "bg-rose-950/80 text-rose-300 border-rose-900"
                  }`}
                >
                  <Info size={16} className="shrink-0 mt-0.5" />
                  <div>{status.message}</div>
                </div>
              )}
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
